/** @jsxImportSource @emotion/react */

// 기타세금 — 부가세/종소세/법인세 납부서 현황 화면.
// 급여관리(PaymentNotice) 화면의 틀을 따르되, 납부서 업로드·알림톡 발송 관련 기능만 남겼다.
// API: payment-notice-api.md §2.10 ~ 2.13 (period = 납부연월, 지급월 → 귀속월 변환 없음)

import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useSearchParams } from "react-router-dom";
import { css } from "@emotion/react";
import { DialogContentText } from "@mui/material";
import BaseButton from "components/common/Button/Base";
import BaseInput from "components/common/Input/Base";
import BaseSelect from "components/common/Select/Base";
import BaseCheck from "components/common/Check/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import ConfirmDialog from "components/common/Dialog/Confirm";
import OtherTaxGrid, {
  needsSend,
  OtherTaxRow,
  OtherTaxSortState
} from "./OtherTaxGrid";
import KakaoSendDialog from "pages/PaymentNotice/Dialog/KakaoSend";
import KakaoHistoryDialog from "pages/PaymentNotice/Dialog/KakaoHistory";
import BusinessDetailDialog from "pages/PaymentNotice/Dialog/BusinessDetail";
import PhoneEditDialog from "pages/PaymentNotice/Dialog/PhoneEdit";
import UploadDialog from "pages/PaymentNotice/Dialog/Upload";
import MatchingDialog from "pages/PaymentNotice/Dialog/Matching";
import ResultDialog from "pages/PaymentNotice/Dialog/Result";
import DuplicateCheckDialog from "pages/PaymentNotice/Dialog/DuplicateCheck";
import { MatchCandidateBusiness } from "pages/PaymentNotice/Dialog/SelectBusiness";
import { PALETTE } from "styles/base/variables";
import { MONTHS, MONTHS_WITH_NEXT } from "data/period.data";
import { reverseArray } from "utils/converter";
import { useMyChargeOnlyFilter } from "utils/hook";
import {
  BusinessWithPaymentNoticeCount,
  OTHER_TAX_TYPE_LABELS,
  OTHER_TAX_TYPES,
  OtherTaxType,
  PaymentNotice as PaymentNoticeData,
  PaymentNoticeUploadResult,
  SimpleBusiness
} from "models/paymentNotice.model";
import { UserInfo } from "models/user.model";
import { getUserOrgId } from "data/user.data";
import {
  checkDuplicatePaymentNotices,
  deleteKakaoMobileNo,
  getOtherTaxPaymentNoticeCounts,
  getSimpleBusinessList,
  getUnmatchedPaymentNotices,
  getUserInfo,
  updateKakaoMobileNo
} from "pages/remotes";

const DEFAULT_PERIOD = MONTHS[MONTHS.length - 1].value;

// 발송 요약 집계에서 클릭해 필터링하는 버킷 (발송 모드의 유형 기준)
//  sent: 발송 완료 / unsent: 미발송 전체 / ready: 납부서 있음·미발송 / noupload: 납부서 없음
type SendBucket = "sent" | "unsent" | "ready" | "noupload";

const sendBucketOf = (
  r: OtherTaxRow,
  type: OtherTaxType
): Exclude<SendBucket, "unsent"> => {
  if (r.sentAt[type]) return "sent";
  return r.counts[type] > 0 ? "ready" : "noupload";
};

const emptyByType = <T,>(v: T): Record<OtherTaxType, T> => ({
  VAT: v,
  GLOBAL_INCOME: v,
  CORPORATE: v
});

// §2.10 응답(유형별 1건씩, 같은 거래처 목록)을 거래처 기준 한 행으로 합친다.
const mergeRows = (
  byType: Record<OtherTaxType, BusinessWithPaymentNoticeCount[]>
): OtherTaxRow[] => {
  const map = new Map<string, OtherTaxRow>();
  OTHER_TAX_TYPES.forEach((type) => {
    byType[type].forEach((b) => {
      let row = map.get(b.id);
      if (!row) {
        row = {
          businessId: b.id,
          name: b.name,
          regNumber: b.regNumber,
          incharge: b.incharge ?? null,
          kakaoMobileNo: b.kakaoMobileNo ?? null,
          userMobileNo: b.userMobileNo ?? null,
          counts: emptyByType(0),
          sentAt: emptyByType<string | null>(null)
        };
        map.set(b.id, row);
      }
      row.counts[type] = b.totalCount || 0;
      row.sentAt[type] = b.kakaoSentAt ?? null;
    });
  });
  return Array.from(map.values());
};

// 어느 한 유형이라도 납부서는 올라왔는데 알림톡을 아직 안 보낸 거래처
const hasUnsent = (r: OtherTaxRow) =>
  OTHER_TAX_TYPES.some((t) => needsSend(r, t));

function OtherTax() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = searchParams.get("period") || DEFAULT_PERIOD;

  const sort: OtherTaxSortState = useMemo(() => {
    const [key, d] = (searchParams.get("sort") || "name,1").split(",");
    return { key: key || "name", dir: d === "-1" ? -1 : 1 };
  }, [searchParams]);

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [rows, setRows] = useState<OtherTaxRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [candidateBusinesses, setCandidateBusinesses] = useState<
    MatchCandidateBusiness[]
  >([]);
  const [unmatchedCount, setUnmatchedCount] = useState(0);

  const [keyword, setKeyword] = useState("");
  const [onlyUnsent, setOnlyUnsent] = useState(false);
  const [isMyChargeOnly, setIsMyChargeOnly] = useMyChargeOnlyFilter();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // 납부서 목록 팝업 (납부서 칸 클릭). null = 닫힘
  const [noticeTarget, setNoticeTarget] = useState<{
    businessId: string;
    type: OtherTaxType;
  } | null>(null);
  // 발송한 알림톡 내용 팝업 (발송 칸 초록일 때 클릭). null = 닫힘
  const [historyTarget, setHistoryTarget] = useState<{
    row: OtherTaxRow;
    type: OtherTaxType;
  } | null>(null);
  // 알림톡 수신 번호 변경 팝업 대상 행 (null = 닫힘)
  const [phoneEditTarget, setPhoneEditTarget] = useState<OtherTaxRow | null>(
    null
  );

  // 납부서 알림톡 발송: 드롭다운에서 유형을 고르면 그 유형의 선택 모드로 들어간다 (null = 꺼짐).
  const [sendType, setSendType] = useState<OtherTaxType | null>(null);
  const [isSendMenuOpen, setIsSendMenuOpen] = useState(false);
  const [sendFilter, setSendFilter] = useState<SendBucket | null>(null);
  // 카톡 발송 진행 다이얼로그 대상 (비어 있으면 닫힘)
  const [kakaoTargets, setKakaoTargets] = useState<
    { id: string; name: string }[]
  >([]);
  // 이미 발송한 거래처가 섞여 있을 때 재확인 팝업에 넘길 행 (비어 있으면 닫힘)
  const [pendingSendRows, setPendingSendRows] = useState<OtherTaxRow[]>([]);

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState<AlertData>({ message: "" });
  const alert = useCallback(
    (message: string, type: AlertData["type"] = "error") => {
      setAlertData({ message, type });
      setIsAlertOpen(true);
    },
    []
  );

  // 업로드/매칭/중복 오케스트레이션 상태 (급여관리 화면 로직과 동일)
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isMatchingOpen, setIsMatchingOpen] = useState(false);
  const [unmatchedList, setUnmatchedList] = useState<PaymentNoticeData[]>([]);
  const [pendingResults, setPendingResults] = useState<
    PaymentNoticeUploadResult[]
  >([]);
  const [pendingDupIds, setPendingDupIds] = useState<string[]>([]);
  const [resultList, setResultList] = useState<PaymentNoticeUploadResult[]>([]);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [flowSource, setFlowSource] = useState<"upload" | "standalone">(
    "upload"
  );
  const [openUploadAfterMatch, setOpenUploadAfterMatch] = useState(false);
  const [isDupOpen, setIsDupOpen] = useState(false);
  const [dupNotices, setDupNotices] = useState<PaymentNoticeData[]>([]);
  const [pendingResultAfterDup, setPendingResultAfterDup] = useState<
    PaymentNoticeUploadResult[]
  >([]);
  const [hasPendingResultAfterDup, setHasPendingResultAfterDup] =
    useState(false);

  useEffect(() => {
    getUserInfo()
      .then((res) => setUserInfo(res.data))
      .catch((err) => console.error(err));
    getSimpleBusinessList()
      .then((res) => {
        const list: SimpleBusiness[] = res.data?.businessDtos || res.data || [];
        setCandidateBusinesses(
          list.map((b) => ({
            id: b.id,
            name: b.name,
            regNumber: b.regNumber
          }))
        );
      })
      .catch((err) => console.error(err));
  }, []);

  const organizationId = getUserOrgId(userInfo);
  const myName = userInfo?.name || "";

  // 월을 빠르게 바꾸면 이전 요청 응답이 늦게 도착해 덮어쓸 수 있어, 마지막 요청만 반영한다.
  const loadSeqRef = useRef(0);
  const loadRows = () => {
    const seq = ++loadSeqRef.current;
    setIsLoading(true);
    Promise.all(
      OTHER_TAX_TYPES.map((type) =>
        getOtherTaxPaymentNoticeCounts(
          period,
          type,
          organizationId || undefined
        ).then((res) => (res.data || []) as BusinessWithPaymentNoticeCount[])
      )
    )
      .then(([vat, globalIncome, corporate]) => {
        if (seq !== loadSeqRef.current) return;
        setRows(
          mergeRows({
            VAT: vat,
            GLOBAL_INCOME: globalIncome,
            CORPORATE: corporate
          })
        );
      })
      .catch((err) => {
        console.error(err);
        if (seq !== loadSeqRef.current) return;
        alert(
          err.response?.data?.message ||
            "거래처 현황을 불러오는 중 오류가 발생했습니다."
        );
      })
      .finally(() => {
        if (seq === loadSeqRef.current) setIsLoading(false);
      });
  };

  const checkUnmatched = () => {
    if (!organizationId) return;
    getUnmatchedPaymentNotices(organizationId, { size: 1 })
      .then((res) => setUnmatchedCount(res.data?.totalElements || 0))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, organizationId]);

  useEffect(() => {
    checkUnmatched();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // ── 필터 + 정렬 ──────────────────────────────────────────────
  // 검색어·내 담당만 보기까지 적용한 목록 (상단 건수는 이 기준)
  const filteredRows = useMemo(() => {
    let list = rows.slice();
    if (isMyChargeOnly && myName)
      list = list.filter((r) => r.incharge === myName);
    if (keyword)
      list = list.filter((r) =>
        (r.name || "").toLowerCase().includes(keyword.toLowerCase())
      );
    return list;
  }, [rows, isMyChargeOnly, myName, keyword]);

  const unsentCount = useMemo(
    () => filteredRows.filter(hasUnsent).length,
    [filteredRows]
  );

  // 발송 요약 집계 (발송 모드 상단, 선택한 유형 기준)
  const sendStats = useMemo(() => {
    const s = { sent: 0, unsent: 0, ready: 0, noupload: 0 };
    if (!sendType) return s;
    filteredRows.forEach((r) => {
      const b = sendBucketOf(r, sendType);
      s[b] += 1;
      if (b !== "sent") s.unsent += 1;
    });
    return s;
  }, [filteredRows, sendType]);

  const visibleRows = useMemo(() => {
    let list = onlyUnsent
      ? filteredRows.filter(hasUnsent)
      : filteredRows.slice();

    if (sendType && sendFilter)
      list = list.filter((r) => {
        const b = sendBucketOf(r, sendType);
        return sendFilter === "unsent" ? b !== "sent" : b === sendFilter;
      });

    const byName = (a: OtherTaxRow, b: OtherTaxRow) =>
      a.name.localeCompare(b.name, "ko");
    const { key, dir } = sort;
    const [kind, rawType] = key.split(":");
    // URL을 직접 고친 경우 등 알 수 없는 유형이면 기본 정렬로 처리한다.
    const type = OTHER_TAX_TYPES.find((t) => t === rawType);
    if (kind === "notice" && type) {
      list.sort(
        (a, b) => dir * (a.counts[type] - b.counts[type]) || byName(a, b)
      );
    } else if (kind === "sent" && type) {
      // 미발송(납부서 있음) → 미발송(납부서 없음) → 발송 완료 순
      const rank = (r: OtherTaxRow) =>
        r.sentAt[type] ? 2 : r.counts[type] > 0 ? 0 : 1;
      list.sort((a, b) => dir * (rank(a) - rank(b)) || byName(a, b));
    } else if (key === "incharge") {
      list.sort(
        (a, b) =>
          dir * (a.incharge || "").localeCompare(b.incharge || "", "ko") ||
          byName(a, b)
      );
    } else {
      list.sort((a, b) => dir * byName(a, b));
    }
    return list;
  }, [filteredRows, onlyUnsent, sort, sendType, sendFilter]);

  // 발송 대상 = 현재 화면에 보이면서 선택된 거래처
  const selectedRows = useMemo(
    () => visibleRows.filter((r) => selected.has(r.businessId)),
    [visibleRows, selected]
  );

  // ── 이벤트 ───────────────────────────────────────────────────
  const setParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next);
  };

  const onSort = (key: string) => {
    if (sort.key === key)
      setParam({ sort: `${key},${sort.dir === 1 ? -1 : 1}` });
    else setParam({ sort: `${key},1` });
  };

  const onToggleSelected = useCallback((businessId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(businessId) ? next.delete(businessId) : next.add(businessId);
      return next;
    });
  }, []);

  const onSelectRange = useCallback(
    (businessIds: string[], checked: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        businessIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
        return next;
      });
    },
    []
  );

  // ── 납부서 카톡 발송 ─────────────────────────────────────────
  const sendTypeLabel = sendType ? OTHER_TAX_TYPE_LABELS[sendType] : "";

  const enterSendMode = (type: OtherTaxType) => {
    setSelected(new Set());
    setSendFilter(null);
    setIsSendMenuOpen(false);
    setSendType(type);
  };

  const exitSendMode = () => {
    setSendType(null);
    setSelected(new Set());
    setSendFilter(null);
  };

  const toggleSendFilter = (b: SendBucket) =>
    setSendFilter((cur) => (cur === b ? null : b));

  // 발송 후 목록이 갱신돼 필터 대상이 사라지면 필터를 자동 해제한다.
  useEffect(() => {
    if (sendFilter && sendStats[sendFilter] === 0) setSendFilter(null);
  }, [sendFilter, sendStats]);

  const openKakaoDialog = (targetRows: OtherTaxRow[]) =>
    setKakaoTargets(
      targetRows.map((r) => ({ id: r.businessId, name: r.name }))
    );

  // 이 유형 납부서가 없는 거래처는 서버가 "해당 기간에 … 납부서가 없습니다"로 실패시키므로
  // 발송 대상에서 빼고, 이미 발송한 거래처가 있으면 재확인 팝업을 띄운다.
  const handleSendClick = () => {
    if (!sendType || !selectedRows.length) return;
    const sendable = selectedRows.filter((r) => r.counts[sendType] > 0);
    if (!sendable.length) {
      alert(`선택한 거래처에 ${sendTypeLabel} 납부서가 없습니다.`);
      return;
    }
    if (
      sendable.length < selectedRows.length ||
      sendable.some((r) => r.sentAt[sendType])
    ) {
      setPendingSendRows(selectedRows);
      return;
    }
    openKakaoDialog(sendable);
  };

  const pendingNoNoticeNames = sendType
    ? pendingSendRows.filter((r) => !r.counts[sendType]).map((r) => r.name)
    : [];
  const pendingResendNames = sendType
    ? pendingSendRows
        .filter((r) => r.counts[sendType] > 0 && r.sentAt[sendType])
        .map((r) => r.name)
    : [];

  const onNoticeCellClick = useCallback(
    (row: OtherTaxRow, type: OtherTaxType) =>
      setNoticeTarget({ businessId: row.businessId, type }),
    []
  );
  const onPhoneClick = useCallback(
    (row: OtherTaxRow) => setPhoneEditTarget(row),
    []
  );
  const onDispatchClick = useCallback(
    (row: OtherTaxRow, type: OtherTaxType) => setHistoryTarget({ row, type }),
    []
  );

  // 알림톡 수신 번호 변경 — 입력값이 있으면 급여담당자 연락처(kakaoMobileNo)로 저장,
  // 비운 채 저장하면 급여담당자 연락처를 삭제해 조직 대표 번호(userMobileNo)가 표시되게 한다.
  const handlePhoneSubmit = (digits: string) => {
    const target = phoneEditTarget;
    if (!target) return;
    const done = () => {
      setPhoneEditTarget(null);
      loadRows();
    };
    const fail = (err: any, action: string) => {
      console.error(err);
      alert(
        err.response?.data?.message || `번호 ${action} 중 오류가 발생했습니다.`
      );
    };

    if (!digits) {
      if (!target.kakaoMobileNo) {
        setPhoneEditTarget(null);
        return;
      }
      deleteKakaoMobileNo(target.businessId)
        .then(done)
        .catch((err) => fail(err, "삭제"));
      return;
    }
    updateKakaoMobileNo(target.businessId, digits)
      .then(done)
      .catch((err) => fail(err, "수정"));
  };

  // ── 업로드/매칭/중복 흐름 (급여관리 화면 로직과 동일) ──────────
  const isUnmatched = (n: PaymentNoticeData) =>
    !n.businessId ||
    n.matchStatus === "UNMATCHED" ||
    n.matchStatus === "AMBIGUOUS";

  const checkDuplicatesAfterRematch = (paymentNoticeId: string) => {
    if (!organizationId) return;
    checkDuplicatePaymentNotices([paymentNoticeId])
      .then((res) => {
        const duplicates: PaymentNoticeData[] = res.data || [];
        if (duplicates.length > 0) {
          setDupNotices(duplicates);
          setIsDupOpen(true);
        }
      })
      .catch((err) => console.error(err));
  };

  const runDupThenResult = (
    ids: string[],
    results: PaymentNoticeUploadResult[]
  ) => {
    if (!ids.length) {
      setResultList(results);
      setIsResultOpen(true);
      return;
    }
    checkDuplicatePaymentNotices(ids)
      .then((res) => {
        const dups: PaymentNoticeData[] = res.data || [];
        if (dups.length) {
          setDupNotices(dups);
          setPendingResultAfterDup(results);
          setHasPendingResultAfterDup(true);
          setIsDupOpen(true);
        } else {
          setResultList(results);
          setIsResultOpen(true);
        }
      })
      .catch(() => {
        setResultList(results);
        setIsResultOpen(true);
      });
  };

  const onUploadFinished = (results: PaymentNoticeUploadResult[]) => {
    setIsUploadOpen(false);
    setFlowSource("upload");
    loadRows();
    checkUnmatched();
    const ids = results
      .filter((r) => r.notice)
      .map((r) => (r.notice as PaymentNoticeData).id);
    const unmatched = results
      .filter((r) => r.notice && isUnmatched(r.notice))
      .map((r) => r.notice as PaymentNoticeData);
    if (unmatched.length) {
      setPendingResults(results);
      setUnmatchedList(unmatched);
      setPendingDupIds(ids);
      setIsMatchingOpen(true);
    } else {
      runDupThenResult(ids, results);
    }
  };

  const onMatchingSuccess = (selections: Record<string, string>) => {
    setIsMatchingOpen(false);
    setUnmatchedList([]);
    loadRows();
    checkUnmatched();
    const updated = pendingResults.map((r) =>
      r.notice && selections[r.notice.id]
        ? {
            ...r,
            notice: {
              ...r.notice,
              businessId: selections[r.notice.id],
              matchStatus: "MATCHED_MANUALLY" as const
            }
          }
        : r
    );
    setPendingResults([]);
    if (flowSource === "upload" && pendingDupIds.length) {
      const ids = pendingDupIds;
      setPendingDupIds([]);
      runDupThenResult(ids, updated);
    } else {
      setResultList(updated);
      setIsResultOpen(true);
    }
  };

  const onMatchingPartialSuccess = (selections: Record<string, string>) => {
    setUnmatchedList((prev) => prev.filter((n) => !selections[n.id]));
    setPendingResults((prev) =>
      prev.map((r) =>
        r.notice && selections[r.notice.id]
          ? {
              ...r,
              notice: {
                ...r.notice,
                businessId: selections[r.notice.id],
                matchStatus: "MATCHED_MANUALLY" as const
              }
            }
          : r
      )
    );
    loadRows();
    checkUnmatched();
  };

  const onNoticeDeleted = (id: string) => {
    setUnmatchedList((prev) => prev.filter((n) => n.id !== id));
    setPendingResults((prev) => prev.filter((r) => r.notice?.id !== id));
    loadRows();
    checkUnmatched();
  };

  // 납부서 업로드 버튼: 매칭 필요 납부서가 있으면 매칭 화면을 먼저 띄우고,
  // 그 화면(취소) 또는 매칭 결과 화면이 닫히면 이어서 업로드 화면을 연다.
  const handleUploadClick = () => {
    if (!organizationId) return alert("소속 조직 정보를 확인할 수 없습니다.");
    if (unmatchedCount <= 0) {
      setIsUploadOpen(true);
      return;
    }
    getUnmatchedPaymentNotices(organizationId, { size: 100 })
      .then((res) => {
        const content: PaymentNoticeData[] = res.data?.content || [];
        if (!content.length) {
          setUnmatchedCount(0);
          setIsUploadOpen(true);
          return;
        }
        setFlowSource("standalone");
        setPendingResults(
          content.map((n) => ({ fileName: n.originalFileName, notice: n }))
        );
        setUnmatchedList(content);
        setOpenUploadAfterMatch(true);
        setIsMatchingOpen(true);
      })
      .catch((err) =>
        alert(
          err.response?.data?.message ||
            "매칭 필요 목록을 조회하는 중 오류가 발생했습니다."
        )
      );
  };

  const continueToUploadIfPending = () => {
    if (openUploadAfterMatch) {
      setOpenUploadAfterMatch(false);
      setIsUploadOpen(true);
    }
  };

  const closeDup = () => {
    setIsDupOpen(false);
    setDupNotices([]);
    if (hasPendingResultAfterDup) {
      setResultList(pendingResultAfterDup);
      setPendingResultAfterDup([]);
      setHasPendingResultAfterDup(false);
      setIsResultOpen(true);
    }
  };

  return (
    <div
      css={css`
        width: min(1640px, calc(100vw - 40px));
        margin-left: calc(50% - min(820px, (100vw - 40px) / 2));
      `}
    >
      <section
        css={css`
          padding: 48px 0;
          display: flex;
          align-items: center;
        `}
      >
        <h1
          css={css`
            font-size: 28px;
            font-weight: 500;
          `}
        >
          전체 업체 현황
        </h1>
      </section>

      <section
        css={css`
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        `}
      >
        <BaseSelect
          name="월별검색"
          options={reverseArray(MONTHS_WITH_NEXT)}
          selectedOption={period}
          onSelect={(e) =>
            setParam({ period: e.currentTarget.value || period })
          }
        />
        <BaseInput
          placeholder="업체명 검색"
          value={keyword}
          onInput={(e) => setKeyword(e.currentTarget.value)}
          css={css`
            box-sizing: border-box;
          `}
        />

        <div
          css={css`
            width: 4px;
          `}
        />

        <BaseButton
          variant="outlined"
          color={unmatchedCount > 0 ? PALETTE.error : ACTION_BTN_COLOR}
          onClick={handleUploadClick}
          css={toolBtnCss}
        >
          납부서 업로드
          {unmatchedCount > 0 ? ` (${unmatchedCount})` : ""}
        </BaseButton>

        {!sendType ? (
          // 호버하면 아래로 부가세/종소세/법인세 버튼이 펼쳐진다.
          <div
            onMouseEnter={() => setIsSendMenuOpen(true)}
            onMouseLeave={() => setIsSendMenuOpen(false)}
            css={css`
              position: relative;
            `}
          >
            <BaseButton
              variant="outlined"
              color={ACTION_BTN_COLOR}
              // 호버가 없는 환경(터치 등)에서도 열 수 있게 클릭으로도 토글한다.
              onClick={() => setIsSendMenuOpen((v) => !v)}
              css={toolBtnCss}
            >
              납부서 알림톡 발송 ▾
            </BaseButton>
            {isSendMenuOpen && (
              <div
                css={css`
                  position: absolute;
                  top: 100%;
                  left: 0;
                  right: 0;
                  z-index: 20;
                  padding-top: 4px;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    padding: 4px;
                    background: ${PALETTE.light};
                    border: 1px solid ${PALETTE.border};
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                  `}
                >
                  {OTHER_TAX_TYPES.map((type) => (
                    <BaseButton
                      key={type}
                      variant="text"
                      onClick={() => enterSendMode(type)}
                      css={css`
                        width: 100%;
                        padding: 10px 12px;
                        font-size: 14px;
                        justify-content: flex-start;
                        &:hover {
                          background-color: ${PALETTE.backgroundDisabled};
                        }
                      `}
                    >
                      {OTHER_TAX_TYPE_LABELS[type]}
                    </BaseButton>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            <span
              css={css`
                font-size: 14px;
                color: ${PALETTE.textMid};
                white-space: nowrap;
              `}
            >
              <b
                css={css`
                  color: ${PALETTE.primary};
                `}
              >
                {sendTypeLabel}
              </b>{" "}
              알림톡 · {selectedRows.length}건 선택됨
            </span>
            <BaseButton
              variant="contained"
              color={PALETTE.primary}
              disabled={selectedRows.length === 0}
              onClick={handleSendClick}
              css={toolBtnCss}
            >
              발송
            </BaseButton>
            <BaseButton
              variant="outlined"
              color={ACTION_BTN_COLOR}
              onClick={exitSendMode}
              css={toolBtnCss}
            >
              취소
            </BaseButton>
          </div>
        )}
      </section>

      <div
        css={css`
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
          font-size: 14px;
          color: ${PALETTE.textMid};

          label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            color: ${PALETTE.textMid};
            cursor: pointer;
          }
        `}
      >
        <span>거래처 {filteredRows.length}개</span>
        <div>
          미발송{" "}
          {unsentCount > 0 || onlyUnsent ? (
            <BaseButton
              variant={onlyUnsent ? "contained" : "outlined"}
              color={PALETTE.error}
              style={{
                padding: "2px 6px 1px",
                fontSize: "13px",
                transform: "translateY(-1px)"
              }}
              onClick={() => setOnlyUnsent((v) => !v)}
            >
              {unsentCount}개
            </BaseButton>
          ) : (
            <span>{unsentCount}개</span>
          )}
        </div>
        <BaseCheck
          id="myChargeOnly"
          checked={isMyChargeOnly}
          onChange={(e) => setIsMyChargeOnly(e.target.checked)}
          label="내 담당만 보기"
        />
      </div>

      {sendType && (
        <div
          css={css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px 14px;
            font-size: 13px;
            color: ${PALETTE.textMid};
            margin-bottom: 8px;
          `}
        >
          <span css={statGroupCss}>
            발송
            <SendCountButton
              count={sendStats.sent}
              active={sendFilter === "sent"}
              onClick={() => toggleSendFilter("sent")}
            />
          </span>

          <span css={statGroupCss}>
            미발송
            <SendCountButton
              count={sendStats.unsent}
              active={sendFilter === "unsent"}
              onClick={() => toggleSendFilter("unsent")}
            />
            <span>( 발송 필요</span>
            <SendCountButton
              count={sendStats.ready}
              active={sendFilter === "ready"}
              onClick={() => toggleSendFilter("ready")}
            />
            <span>/ 미업로드</span>
            <SendCountButton
              count={sendStats.noupload}
              active={sendFilter === "noupload"}
              onClick={() => toggleSendFilter("noupload")}
            />
            <span>)</span>
          </span>

          <span
            css={css`
              padding-left: 14px;
              border-left: 1px solid ${PALETTE.border};
            `}
          >
            선택 {selectedRows.length}곳
          </span>
          {selectedRows.length === 0 && (
            <span
              css={css`
                color: ${PALETTE.primary};
              `}
            >
              {sendTypeLabel} 알림톡을 발송할 거래처를 선택하세요
            </span>
          )}
        </div>
      )}

      <OtherTaxGrid
        rows={visibleRows}
        loading={isLoading}
        sort={sort}
        onSort={onSort}
        selectable={!!sendType}
        highlightType={sendType}
        selected={selected}
        onToggleSelected={onToggleSelected}
        onToggleAll={(checked) =>
          setSelected(
            checked ? new Set(visibleRows.map((r) => r.businessId)) : new Set()
          )
        }
        onSelectRange={onSelectRange}
        onNoticeCellClick={onNoticeCellClick}
        onPhoneClick={onPhoneClick}
        onDispatchClick={onDispatchClick}
      />

      <div
        css={css`
          margin-top: 8px;
          display: flex;
          flex-wrap: wrap;
          gap: 4px 16px;
          padding: 8px 12px;
          background: ${PALETTE.light};
          border: 1px solid ${PALETTE.border};
          border-radius: 10px;
          font-size: 12px;
          color: ${PALETTE.textMid};
        `}
      >
        <LegendItem
          dot={<LegendDot color={PALETTE.progressCompleted} filled />}
          label="완료"
          desc="납부서 첨부 · 알림톡 발송"
        />
        <LegendItem
          dot={<LegendDot color="#F5A623" filled={false} />}
          label="미발송"
          desc="납부서는 있으나 알림톡 미발송"
        />
        <LegendItem
          dot={
            <span
              css={css`
                color: ${PALETTE.textSub};
              `}
            >
              –
            </span>
          }
          label="해당없음"
          desc="납부서 없음"
        />
      </div>

      <BusinessDetailDialog
        isOpen={noticeTarget !== null}
        businessId={noticeTarget?.businessId || null}
        organizationId={organizationId}
        period={period}
        taxType={noticeTarget?.type}
        businessList={candidateBusinesses}
        onClose={() => setNoticeTarget(null)}
        onNoticeDeleted={() => {
          loadRows();
          checkUnmatched();
        }}
        onNoticeRematched={(paymentNoticeId) => {
          loadRows();
          checkUnmatched();
          checkDuplicatesAfterRematch(paymentNoticeId);
        }}
      />

      <KakaoHistoryDialog
        isOpen={historyTarget !== null}
        businessId={historyTarget?.row.businessId || null}
        businessName={
          historyTarget
            ? `${historyTarget.row.name} · ${
                OTHER_TAX_TYPE_LABELS[historyTarget.type]
              }`
            : ""
        }
        period={period}
        taxType={historyTarget?.type}
        onClose={() => setHistoryTarget(null)}
      />

      <PhoneEditDialog
        isOpen={phoneEditTarget !== null}
        businessName={phoneEditTarget?.name || ""}
        currentNumber={
          phoneEditTarget
            ? phoneEditTarget.kakaoMobileNo ||
              phoneEditTarget.userMobileNo ||
              null
            : null
        }
        hasKakaoMobileNo={!!phoneEditTarget?.kakaoMobileNo}
        onClose={() => setPhoneEditTarget(null)}
        onSubmit={handlePhoneSubmit}
      />

      <ConfirmDialog
        isOpen={pendingSendRows.length > 0}
        onCancel={() => setPendingSendRows([])}
        onConfirm={() => {
          openKakaoDialog(
            pendingSendRows.filter((r) => sendType && r.counts[sendType] > 0)
          );
          setPendingSendRows([]);
        }}
        confirmLabel="발송"
      >
        {pendingNoNoticeNames.length > 0 && (
          <>
            <DialogContentText>
              {sendTypeLabel} 납부서가 없는 거래처 {pendingNoNoticeNames.length}
              곳은 발송 대상에서 제외됩니다.
            </DialogContentText>
            <DialogContentText>
              ({pendingNoNoticeNames.join(", ")})
            </DialogContentText>
          </>
        )}
        {pendingResendNames.length > 0 && (
          <>
            <DialogContentText>
              이미 {sendTypeLabel} 알림톡을 발송한 거래처가 포함되어 있습니다.
            </DialogContentText>
            <DialogContentText>
              ({pendingResendNames.join(", ")})
            </DialogContentText>
          </>
        )}
        <DialogContentText>발송하시겠습니까?</DialogContentText>
      </ConfirmDialog>

      <KakaoSendDialog
        isOpen={kakaoTargets.length > 0}
        businesses={kakaoTargets}
        period={period}
        taxType={sendType || undefined}
        onClose={() => {
          setKakaoTargets([]);
          exitSendMode();
        }}
        onFinished={loadRows}
      />

      <UploadDialog
        isOpen={isUploadOpen}
        organizationId={organizationId}
        onClose={() => setIsUploadOpen(false)}
        onUploadFinished={onUploadFinished}
      />
      <MatchingDialog
        isOpen={isMatchingOpen}
        organizationId={organizationId}
        unmatchedList={unmatchedList}
        businessList={candidateBusinesses}
        onClose={() => {
          setIsMatchingOpen(false);
          setUnmatchedList([]);
          setPendingResults([]);
          continueToUploadIfPending();
        }}
        onSuccess={onMatchingSuccess}
        onPartialSuccess={onMatchingPartialSuccess}
        onDeleted={onNoticeDeleted}
        showCloseButton={flowSource === "standalone"}
      />
      <DuplicateCheckDialog
        isOpen={isDupOpen}
        organizationId={organizationId}
        duplicateList={dupNotices}
        businessList={candidateBusinesses}
        onClose={closeDup}
        onChanged={() => {
          loadRows();
          checkUnmatched();
        }}
      />
      <ResultDialog
        isOpen={isResultOpen}
        title={flowSource === "upload" ? "업로드 결과" : "매칭 결과"}
        results={resultList}
        onClose={() => {
          setIsResultOpen(false);
          setResultList([]);
          continueToUploadIfPending();
        }}
      />

      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertData}
      />
    </div>
  );
}

const toolBtnCss = css`
  padding: 11px 16px;
  font-size: 14px;
  white-space: nowrap;
`;

const statGroupCss = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

// 오른쪽 액션 버튼(업로드 · 알림톡 발송) 공통 색 — 급여관리 화면과 동일
const ACTION_BTN_COLOR = "#000000";

function SendCountButton({
  count,
  active,
  onClick
}: {
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <BaseButton
      variant={active ? "contained" : "outlined"}
      color={PALETTE.primary}
      style={{
        padding: "2px 6px 1px",
        fontSize: "13px",
        transform: "translateY(-1px)"
      }}
      onClick={onClick}
    >
      {`${count}개`}
    </BaseButton>
  );
}

function LegendDot({ color, filled }: { color: string; filled: boolean }) {
  return (
    <span
      css={css`
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 3px;
        ${filled
          ? `background:${color};`
          : `box-shadow: inset 0 0 0 2px ${color};`}
      `}
    />
  );
}

function LegendItem({
  dot,
  label,
  desc
}: {
  dot: ReactNode;
  label: string;
  desc: string;
}) {
  return (
    <span
      css={css`
        display: flex;
        align-items: center;
        gap: 6px;
      `}
    >
      {dot}
      <b
        css={css`
          color: ${PALETTE.text};
        `}
      >
        {label}
      </b>{" "}
      {desc}
    </span>
  );
}

export default OtherTax;
